/**
 * Shared audience controls for report sharing — a checkbox list of people
 * (staff users OR user groups) and a single role/level bucket checkbox.
 *
 * Extracted from ReportBuilderWizard so both the CST wizard and the AI-report
 * share modal use one implementation.
 */
import type { ReportAudiencePerson } from '@/types/customReports.types';

export function AudienceBucketCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

export function PersonMultiSelect({
  label,
  people,
  value,
  onChange,
  emptyLabel,
}: {
  label: string;
  people: ReportAudiencePerson[];
  value: string[];
  onChange: (v: string[]) => void;
  /** Overrides the default "No {label} available" empty message. */
  emptyLabel?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">
        {label}{' '}
        <span className="text-xs font-normal text-slate-400">({value.length} selected)</span>
      </label>
      {people.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-2 text-xs text-slate-500">
          {emptyLabel ?? `No ${label.toLowerCase()} available`}
        </div>
      ) : (
        <div className="max-h-40 overflow-y-auto rounded-md border border-slate-300 bg-white p-2">
          {people.map((p) => {
            const id = String(p.id);
            const checked = value.includes(id);
            return (
              <label key={id} className="flex cursor-pointer items-center gap-2 py-0.5 text-sm">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    if (e.target.checked) onChange([...value, id]);
                    else onChange(value.filter((v) => v !== id));
                  }}
                />
                <span className="text-slate-700">{p.name}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
